import React, { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import CustomInput from "../../components/input/CustomInput";
import { motion } from "framer-motion";
import { Eye, SlashedEye } from "../../components/svg/Auth/Auth";
import { useAuth } from "../../api/auth";
import Button from "../../components/button/Button";
import clsx from "clsx";
import { Loader } from "../../components/loader/Loader";
import { AuthLayout } from "../../layout/AuthLayout";
import { useForgotPassword } from "../../api/forgotPassword";
import SEO from "../../components/SEO/SEO";

function SignIn() {
  const navigate = useNavigate();
  const location = useLocation();
  const [viewPassword, setViewPassword] = useState(false);
  const {
    handleSignIn,
    handleCheckAudience,
    email,
    setEmail,
    password,
    setPassword,
    loading,
    show,
    setShow,
  } = useAuth();

  const { pending, handleForgotPassword, setEmailAddress } =
    useForgotPassword();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams) {
      setEmail(searchParams.get("mobile"));
    }
    const toks = localStorage.getItem("arena_xp_token");
    const storedSlug = localStorage.getItem("world_slug");
    const data = searchParams.get("data");
    localStorage.setItem("arena_hashed_d", encodeURIComponent(data));
    const urlDecoded = atob(decodeURIComponent(encodeURIComponent(data)));
    // 2. Base64 decode
    const decoded = urlDecoded;
    const decodedArr = decoded.split("|");
    if (decodedArr.length === 3) {
      const campaignId = decodedArr[0];
      const gameId = decodedArr[1];
      const userId = decodedArr[2];
      const expires = searchParams.get("expires");
      const signature = searchParams.get("signature");
      if (location.search !== "") {
        if (toks === "null" || toks === null || !toks) {
          return;
        } else {
          navigate(
            `/campaign_id/${campaignId}/game_id/${gameId}?userId=${userId}&expires=${expires}&signature=${signature}`
          );
        }
      }
    } else {
      const brandId = decodedArr[0];
      const userId = decodedArr[1];
      const expires = searchParams.get("expires");
      const signature = searchParams.get("signature");
      if (location.search !== "") {
        if (toks === "null" || toks === null || !toks) {
          return;
        } else {
          navigate(
            `/worlds/${storedSlug}?id=${brandId}&userId=${userId}&expires=${expires}&signature=${signature}`
          );
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!email) {
      setShow(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  return (
    <AuthLayout>
      <SEO
        title="Sign In to Arena - Start Playing & Winning"
        description="Sign in to Arena and access 100+ exciting games. Start your gaming journey and win amazing rewards. Join millions of players in the ultimate gamification experience."
        keywords="arena sign in, login, gaming platform, online games, win rewards, player account"
        url="/"
      />
      <div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 2, delay: 0.5 }}
          className="flex flex-col main_gradient h-[400px] items-center justify-center rounded-b-[50px]"
        >
          <motion.h3
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.6 }}
            className="text-2xl tracking-tighter font-semibold text-center text-white mx-0"
          >
            Welcome to Arena
          </motion.h3>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.7 }}
            className="text-base font-medium text-white"
          >
            Get ready to win exciting rewards.
          </motion.p>

          <img
            className="max-w-[260px] object-contain mt-5"
            src="/image/home_image.png"
            alt="banner"
            loading="lazy"
          />
        </motion.div>
        <div className="container grid place-items-center pb-12">
          <motion.h3
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.6 }}
            className="text-2xl tracking-tighter font-semibold text-center text-black mt-12 880:mt-14 mb-1 mx-0"
          >
            You’re Just One Step Away!
          </motion.h3>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.7 }}
            className="text-base text-black text-center max-w-[330px] mx-auto"
          >
            Fill in your info below & let the fun begin.
          </motion.p>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.7 }}
            className="mt-8 mb-4 w-full px-1"
          >
            <CustomInput
              placeholder="Enter phone number of email"
              type="text"
              name="email"
              value={email}
              onchange={(e) => {
                setEmail(e.target.value);
                setEmailAddress(e.target.value);
              }}
              inputLabel={email && "Email"}
            />
            {show && (
              <CustomInput
                placeholder="Password"
                type={viewPassword ? "text" : "password"}
                name="password"
                value={password}
                autoComplete="off"
                required={true}
                trailingIcon={viewPassword ? <Eye /> : <SlashedEye />}
                onclick={() => setViewPassword(!viewPassword)}
                onchange={(e) => setPassword(e.target.value)}
                inputLabel={password && "Password"}
              />
            )}
            {show && (
              <div className="flex justify-end">
                <button
                  className="text-[13px] text-primary-main"
                  onClick={() => handleForgotPassword()}
                >
                  {pending ? (
                    <Loader
                      width={"35px"}
                      height={"25px"}
                      color={"#2741D6"}
                      type="bubbles"
                    />
                  ) : (
                    "Forgot Password?"
                  )}
                </button>
              </div>
            )}
            <div>
              <p className="text-[11px] text-[#00000080] mt-1">
                By continuing, you agree to Arena’s{" "}
                <NavLink to={"#"} className={"text-primary-main underline"}>
                  Terms of Use
                </NavLink>{" "}
                and{" "}
                <NavLink to={"#"} className={"text-primary-main underline"}>
                  Privacy Policy
                </NavLink>
              </p>
            </div>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.8 }}
              className="mt-12"
            >
              <Button
                text={loading ? <Loader type="bubbles" /> : "Continue"}
                className={clsx(loading && "!py-0")}
                onClick={() => {
                  if (!show) {
                    handleCheckAudience();
                  } else {
                    handleSignIn();
                  }
                }}
                disabled={!show ? !email : !password }
              />
            </motion.div>
          </motion.div>
        </div>
      </div>
    </AuthLayout>
  );
}

export default SignIn;
